// Translation helper for code that runs OUTSIDE a Vue component setup context
// (composables, Pinia stores, utils, the $fetch wrapper). Inside a component
// template, use the auto-imported `$t` / `useI18n()` instead.
//
// Degrades to the raw key if the i18n instance isn't reachable, so callers
// never crash and always render something deterministic.

interface I18nLike {
  t: (key: string, params?: Record<string, unknown>) => string
  te?: (key: string) => boolean
  locale?: { value?: string } | string
}

export function translate(key: string, params?: Record<string, unknown>): string {
  try {
    // useNuxtApp is a Nuxt auto-import (global at runtime).
    const nuxtApp = (useNuxtApp as unknown as () => { $i18n?: I18nLike })()
    const i18n = nuxtApp?.$i18n
    if (i18n && typeof i18n.t === 'function') {
      return i18n.t(key, params ?? {})
    }
  } catch {
    // Outside Nuxt context (e.g. unit tests) — fall through to key.
  }
  return key
}

/**
 * Resolve the active locale code (e.g. 'en', 'uk') for code that runs OUTSIDE
 * a Vue component setup context — utils like format.ts that feed locale to the
 * Intl APIs so dates/numbers render with the right month names and 12h/24h
 * clock. Degrades to 'en' when the i18n instance isn't reachable (unit tests,
 * early SSR) so callers never crash.
 */
export function activeLocale(fallback = 'en'): string {
  try {
    const nuxtApp = (useNuxtApp as unknown as () => { $i18n?: I18nLike })()
    const loc = nuxtApp?.$i18n?.locale
    if (typeof loc === 'string') return loc || fallback
    if (loc && typeof loc.value === 'string') return loc.value || fallback
  } catch {
    // Outside Nuxt context — fall through to fallback.
  }
  return fallback
}

/**
 * True when the active i18n instance has a message for `key`. Used by util
 * code (e.g. API-error localization) to decide whether to translate a backend
 * error code or fall back to the backend's English message. Degrades to false
 * outside Nuxt context (unit tests / early SSR).
 */
export function hasTranslation(key: string): boolean {
  try {
    const nuxtApp = (useNuxtApp as unknown as () => { $i18n?: I18nLike })()
    const te = nuxtApp?.$i18n?.te
    if (typeof te === 'function') return te(key)
  } catch {
    // Outside Nuxt context — no i18n available.
  }
  return false
}
